/** Iter 104 — Vehicle Financial Reports: revenue & expenses per vehicle,
 * commission separation, profit ranking, utilization, charts, PDF/CSV export. */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import VehicleExpensesPanel, { CATEGORY_LABELS } from "@/components/vendor/VehicleExpensesPanel";
import { FileDown, FileSpreadsheet, TrendingUp, TrendingDown, Loader2, Mail } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const QUICK = ["Today", "This week", "This month", "Last month", "This year", "Custom"];
const REV_SOURCES = [
  ["rental", "Rental"], ["insurance", "Insurance"], ["extras", "Extras"],
  ["delivery_fees", "Delivery / one-way"], ["late_return", "Late return"],
  ["fuel", "Fuel charges"], ["cleaning", "Cleaning"], ["damage", "Damage"],
  ["other", "Other"], ["refunds", "Refunds (−)"],
];

function quickRange(key) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (key === "Today") return [iso(now), iso(now)];
  if (key === "This week") {
    const d = new Date(now);
    d.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    return [iso(d), iso(now)];
  }
  if (key === "This month") return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
  if (key === "Last month") {
    return [iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), iso(new Date(now.getFullYear(), now.getMonth(), 0))];
  }
  if (key === "This year") return [iso(new Date(now.getFullYear(), 0, 1)), iso(now)];
  return null;
}

function Card({ label, value, tone = "", testId }) {
  return (
    <div className={`rounded-2xl border border-border bg-white p-3.5 ${tone}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className="text-lg font-display font-bold mt-0.5">{value}</div>
    </div>
  );
}

export default function VehicleReportsTab({ cars }) {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const pdfLang = (i18n?.language || "en").startsWith("sq") ? "sq" : "en";
  const [carSel, setCarSel] = useState(() => {
    const c = searchParams.get("car");
    return c ? [c] : [];
  });
  const [quick, setQuick] = useState("This month");
  const [[dateFrom, dateTo], setRange] = useState(() => quickRange("This month"));
  const [recognition, setRecognition] = useState("pickup");
  const [statusF, setStatusF] = useState("");
  const [paymentF, setPaymentF] = useState("");
  const [rank, setRank] = useState("profit");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (carSel.length) p.set("car_ids", carSel.join(","));
    p.set("date_from", dateFrom);
    p.set("date_to", dateTo);
    p.set("recognition", recognition);
    if (statusF) p.set("status", statusF);
    if (paymentF) p.set("payment_status", paymentF);
    return p;
  }, [carSel, dateFrom, dateTo, recognition, statusF, paymentF]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/vendor/reports/vehicles?${params}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const setQuickKey = (k) => {
    setQuick(k);
    const r = quickRange(k);
    if (r) setRange(r);
  };

  const toggleCar = (id) => {
    setCarSel((s) => (id === "" ? [] : s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const download = async (path, filename) => {
    try {
      const res = await fetch(`${API}${path}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const { data: r } = await api.post(`/vendor/reports/email-digest?${params}&lang=${pdfLang}`);
      toast.success(`Report emailed to ${r.sent_to}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setEmailing(false); }
  };

  const t = data?.totals;
  const vehicles = data?.vehicles || [];
  const single = vehicles.length === 1 ? vehicles[0] : null;
  const ranked = useMemo(() => {
    const arr = [...vehicles];
    if (rank === "revenue") arr.sort((a, b) => b.revenue.vendor_net - a.revenue.vendor_net);
    else if (rank === "margin") arr.sort((a, b) => b.margin - a.margin);
    else if (rank === "per_day") arr.sort((a, b) => b.avg_per_day - a.avg_per_day);
    else arr.sort((a, b) => b.profit - a.profit);
    return arr;
  }, [vehicles, rank]);

  const chartVehicles = vehicles.slice(0, 10).map((v) => ({
    name: `${v.make || ""} ${v.model || ""}`.trim().slice(0, 14),
    Revenue: v.revenue.vendor_net, Profit: v.profit, Expenses: v.expenses_total,
  }));

  return (
    <div className="space-y-5" data-testid="vehicle-reports-tab">
      <div>
        <h2 className="font-display text-2xl font-semibold tracking-tight">Vehicle Financial Reports</h2>
        <p className="text-sm text-neutral-500 mt-1">How much each vehicle earned, what it cost you, and which cars make you the most money.</p>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-border bg-white p-4 space-y-3" data-testid="report-filters">
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map((k) => (
            <button key={k} type="button" onClick={() => setQuickKey(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${quick === k ? "bg-neutral-900 text-white border-neutral-900" : "border-border text-neutral-600 hover:border-neutral-400"}`}
              data-testid={`quick-${k.toLowerCase().replace(/ /g, "-")}`}>
              {k}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div>
            <div className="text-[10px] uppercase font-bold text-neutral-500 mb-1">From</div>
            <Input type="date" value={dateFrom} onChange={(e) => { setQuick("Custom"); setRange([e.target.value, dateTo]); }} className="h-9" data-testid="report-date-from" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-neutral-500 mb-1">To</div>
            <Input type="date" value={dateTo} onChange={(e) => { setQuick("Custom"); setRange([dateFrom, e.target.value]); }} className="h-9" data-testid="report-date-to" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-neutral-500 mb-1">Revenue recognition</div>
            <select value={recognition} onChange={(e) => setRecognition(e.target.value)} className="w-full h-9 rounded-lg border border-border px-2 text-sm" data-testid="report-recognition">
              <option value="payment">By payment date</option>
              <option value="pickup">By pickup date</option>
              <option value="completion">By completion date</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-neutral-500 mb-1">Status filters</div>
            <div className="flex gap-1.5">
              <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="w-1/2 h-9 rounded-lg border border-border px-1.5 text-xs" data-testid="report-status">
                <option value="">All statuses</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
              </select>
              <select value={paymentF} onChange={(e) => setPaymentF(e.target.value)} className="w-1/2 h-9 rounded-lg border border-border px-1.5 text-xs" data-testid="report-payment-status">
                <option value="">All payments</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold text-neutral-500 mb-1.5">Vehicles</div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => toggleCar("")}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${carSel.length === 0 ? "bg-blue-600 text-white border-blue-600" : "border-border text-neutral-600"}`}
              data-testid="car-filter-all">All vehicles</button>
            {(cars || []).map((c) => (
              <button key={c.id} type="button" onClick={() => toggleCar(c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border ${carSel.includes(c.id) ? "bg-blue-600 text-white border-blue-600" : "border-border text-neutral-600"}`}
                data-testid={`car-filter-${c.id}`}>
                {c.make} {c.model}{c.license_plate ? ` · ${c.license_plate}` : ""}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => download(`/vendor/reports/vehicles/export.pdf?${params}&lang=${pdfLang}`, `vehicle-report-${dateFrom}_${dateTo}.pdf`)} data-testid="export-pdf-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" /> Export PDF
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={emailReport} disabled={emailing} data-testid="email-report-btn">
            {emailing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />} Email me this report
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => download(`/vendor/reports/vehicles/export.csv?type=summary&${params}`, `vehicle-summary-${dateFrom}_${dateTo}.csv`)} data-testid="export-csv-btn">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
          <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => download(`/vendor/reports/vehicles/export.csv?type=revenue&${params}`, `revenue-transactions-${dateFrom}_${dateTo}.csv`)} data-testid="export-revenue-csv">Revenue CSV</Button>
          <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => download(`/vendor/reports/vehicles/export.csv?type=expenses&${params}`, `expenses-${dateFrom}_${dateTo}.csv`)} data-testid="export-expenses-csv">Expenses CSV</Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>
      ) : t ? (
        <>
          {/* Summary cards — commission separation (spec §5) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="report-summary-cards">
            <Card label="Gross revenue" value={<Money amount={t.revenue.gross} />} testId="card-gross" />
            <Card label="Platform commission" value={<span className="text-rose-600">−<Money amount={t.revenue.commission} /></span>} testId="card-commission" />
            <Card label="Vendor revenue" value={<Money amount={t.revenue.vendor_net} />} testId="card-vendor-net" />
            <Card label="Expenses" value={<span className="text-rose-600">−<Money amount={t.expenses_total} /></span>} testId="card-expenses" />
            <Card label="Net profit" value={<span className={t.profit >= 0 ? "text-emerald-600" : "text-rose-600"}><Money amount={t.profit} /></span>} testId="card-profit" />
            <Card label="Margin" value={`${t.margin}%`} testId="card-margin" />
          </div>
          <div className="text-xs text-neutral-500" data-testid="report-meta">
            {t.reservations} reservations · {t.rental_days} rental days · Deposits (excluded from revenue): held <Money amount={data.deposits.held} /> / refunded <Money amount={data.deposits.refunded} /> / deducted <Money amount={data.deposits.deducted} />
          </div>

          {/* Charts */}
          {(data.monthly || []).length > 1 && (
            <div className="rounded-2xl border border-border bg-white p-4" data-testid="monthly-chart">
              <div className="text-sm font-bold mb-2">Month by month</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => `€${Number(v).toFixed(2)}`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="profit" name="Profit" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {chartVehicles.length > 1 && (
            <div className="rounded-2xl border border-border bg-white p-4" data-testid="vehicle-chart">
              <div className="text-sm font-bold mb-2">Revenue & profit by vehicle</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartVehicles} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-18} height={44} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => `€${Number(v).toFixed(2)}`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Profit" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Single-vehicle detail (spec §6 + §11 + §12) */}
          {single && (
            <div className="rounded-2xl border border-border bg-white p-4" data-testid="single-vehicle-detail">
              <div className="flex items-start gap-3 mb-3">
                {single.image && <img src={single.image} alt="" className="h-16 w-24 rounded-xl object-cover ring-1 ring-border" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                <div>
                  <div className="font-bold">{single.make} {single.model} {single.license_plate ? `· ${single.license_plate}` : ""}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {single.reservations} reservations · {single.rental_days} rental days · Utilization <span className="font-bold">{single.utilization.rate}%</span> ({single.utilization.rented_days}/{single.utilization.available_days} available days)
                  </div>
                  <div className="text-xs text-neutral-500">
                    Avg / rental day <span className="font-bold"><Money amount={single.avg_per_day} /></span> · Avg / booking <span className="font-bold"><Money amount={single.avg_per_booking} /></span>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-[11px] uppercase font-bold text-neutral-500 mb-1.5">Revenue sources</div>
                  {REV_SOURCES.filter(([k]) => single.revenue[k] > 0).map(([k, label]) => (
                    <div key={k} className="flex justify-between text-sm py-1 border-b border-dashed border-border last:border-0">
                      <span className="text-neutral-600">{label}</span>
                      <span className={`font-bold ${k === "refunds" ? "text-rose-600" : ""}`}>{k === "refunds" ? "−" : ""}<Money amount={single.revenue[k]} /></span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm py-1.5 font-bold"><span>Total revenue</span><Money amount={single.revenue.gross} /></div>
                </div>
                <div>
                  <div className="text-[11px] uppercase font-bold text-neutral-500 mb-1.5">Expenses by category</div>
                  {Object.entries(single.expenses_by_category).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm py-1 border-b border-dashed border-border last:border-0">
                      <span className="text-neutral-600">{CATEGORY_LABELS[k] || k}</span>
                      <span className="font-bold text-rose-600">−<Money amount={v} /></span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm py-1.5 font-bold"><span>Total expenses</span><span className="text-rose-600">−<Money amount={single.expenses_total} /></span></div>
                </div>
              </div>
            </div>
          )}

          {/* Comparison table (spec §7) */}
          <div className="rounded-2xl border border-border bg-white overflow-x-auto" data-testid="vehicles-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-neutral-50/60 text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2.5 font-bold">Vehicle</th>
                  <th className="text-right px-3 py-2.5 font-bold">Res.</th>
                  <th className="text-right px-3 py-2.5 font-bold">Days</th>
                  <th className="text-right px-3 py-2.5 font-bold">Gross</th>
                  <th className="text-right px-3 py-2.5 font-bold">Commission</th>
                  <th className="text-right px-3 py-2.5 font-bold">Vendor net</th>
                  <th className="text-right px-3 py-2.5 font-bold">Expenses</th>
                  <th className="text-right px-3 py-2.5 font-bold">Profit</th>
                  <th className="text-right px-3 py-2.5 font-bold">Util.</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.car_id} className="border-t border-border cursor-pointer hover:bg-neutral-50" onClick={() => setCarSel([v.car_id])} data-testid={`report-row-${v.car_id}`}>
                    <td className="px-3 py-2.5 font-semibold">{v.make} {v.model}<div className="text-[10px] text-neutral-400 font-normal">{v.license_plate}</div></td>
                    <td className="px-3 py-2.5 text-right">{v.reservations}</td>
                    <td className="px-3 py-2.5 text-right">{v.rental_days}</td>
                    <td className="px-3 py-2.5 text-right"><Money amount={v.revenue.gross} /></td>
                    <td className="px-3 py-2.5 text-right text-rose-600">−<Money amount={v.revenue.commission} /></td>
                    <td className="px-3 py-2.5 text-right"><Money amount={v.revenue.vendor_net} /></td>
                    <td className="px-3 py-2.5 text-right text-rose-600">−<Money amount={v.expenses_total} /></td>
                    <td className={`px-3 py-2.5 text-right font-bold ${v.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}><Money amount={v.profit} /></td>
                    <td className="px-3 py-2.5 text-right">{v.utilization.rate}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-neutral-200 bg-neutral-50/60 font-bold" data-testid="fleet-totals-row">
                  <td className="px-3 py-2.5">Fleet total</td>
                  <td className="px-3 py-2.5 text-right">{t.reservations}</td>
                  <td className="px-3 py-2.5 text-right">{t.rental_days}</td>
                  <td className="px-3 py-2.5 text-right"><Money amount={t.revenue.gross} /></td>
                  <td className="px-3 py-2.5 text-right text-rose-600">−<Money amount={t.revenue.commission} /></td>
                  <td className="px-3 py-2.5 text-right"><Money amount={t.revenue.vendor_net} /></td>
                  <td className="px-3 py-2.5 text-right text-rose-600">−<Money amount={t.expenses_total} /></td>
                  <td className={`px-3 py-2.5 text-right ${t.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}><Money amount={t.profit} /></td>
                  <td className="px-3 py-2.5" />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Ranking (spec §13) */}
          {vehicles.length > 1 && (
            <div className="rounded-2xl border border-border bg-white p-4" data-testid="ranking-panel">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="text-sm font-bold flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-emerald-600" /> Vehicle profitability ranking</div>
                <select value={rank} onChange={(e) => setRank(e.target.value)} className="h-8 rounded-lg border border-border px-2 text-xs" data-testid="ranking-sort">
                  <option value="profit">By net profit</option>
                  <option value="revenue">By revenue</option>
                  <option value="margin">By profit margin</option>
                  <option value="per_day">By revenue per rental day</option>
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] uppercase font-bold text-emerald-700 mb-1">Most profitable</div>
                  {ranked.slice(0, 3).map((v, i) => (
                    <div key={v.car_id} className="flex justify-between text-sm py-1" data-testid={`rank-top-${i}`}>
                      <span>{i + 1}. {v.make} {v.model}</span>
                      <span className="font-bold text-emerald-600"><Money amount={v.profit} /> · {v.margin}%</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[11px] uppercase font-bold text-rose-700 mb-1 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Least profitable</div>
                  {[...ranked].reverse().slice(0, 3).map((v, i) => (
                    <div key={v.car_id} className="flex justify-between text-sm py-1" data-testid={`rank-bottom-${i}`}>
                      <span>{v.make} {v.model}</span>
                      <span className={`font-bold ${v.profit >= 0 ? "" : "text-rose-600"}`}><Money amount={v.profit} /> · {v.margin}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}

      {/* Expense manager (spec §3, §15, §21, §27) */}
      <VehicleExpensesPanel
        cars={cars}
        dateFrom={dateFrom}
        dateTo={dateTo}
        carFilter={carSel.length === 1 ? carSel[0] : null}
        onChanged={load}
      />
    </div>
  );
}
